import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Input } from 'reactstrap'
import useWebSocket from 'react-use-websocket'
import StateButton from '../common/StateButton'
import BounceIndicador from './BounceIndicador'
import CheckerResults from './CheckerResults'
import { checkResults, getWebSocketURL, startCheck } from './lib/connections'
import selectProperData, { genAttachPayload, genTickPayload } from './lib/helpers'

// A cada x segundos vamos verificar se o processo solicitado já finalizou.
const TICK_INTERVAL = 1000 * 10;

// É necessário esperar um tempo para que internamente tudo seja finalizado corretamente.
const ENABLE_INTERVAL = 1000 * 2;

const LONG_LABEL = ' Pode demorar!';

function CopycheckerElement() {
  const [content, setContent] = React.useState('')
  const [jobID, setJobID] = React.useState('')
  const [isLoading, setIsLoading] = React.useState(false)
  const [percentage, setPercentage] = React.useState(0)
  const [summary, setSummary] = React.useState({})
  const [contentEnabled, setContentEnabled] = React.useState(false)

  const ticker = React.useRef(null);
  const { sendJsonMessage, lastMessage } = useWebSocket(getWebSocketURL, {
    // eslint-disable-next-line no-use-before-define
    onOpen: onOpenWebSocketConnection,
    onClose: () => setTimeout(() => setContentEnabled(true), ENABLE_INTERVAL),
  }, !!jobID);

  function onOpenWebSocketConnection() {
    ticker.current = setInterval(() => sendJsonMessage(genTickPayload(jobID)), TICK_INTERVAL)
    sendJsonMessage(genAttachPayload(jobID))
  }

  function processSummaryContent(summary) {
    const services = Object.keys(summary);

    if (services.length === 0) {
      return [0, 0]
    }

    const servicesDone = services.filter(key => summary[key] === 'done').length;
    const completionPercentage = parseFloat((servicesDone / services.length) * 100).toFixed(1);

    setPercentage(value => Math.max(value, completionPercentage));

    return [servicesDone, services.length]
  }

  React.useEffect(() => {
    if (lastMessage === null) return;

    const message = JSON.parse(lastMessage.data || '{}');

    switch (message.type) {
      case 'summary':
        setSummary(message.summary);
        break;

      case 'scraper-done':
        setSummary(prev => ({ ...prev, ...{ [message.id]: 'done' } }));
        break;

      case 'scraper-discovery':
        setSummary(prev => ({
          ...prev,
          ...message.ids.reduce((memo, id) => {
            memo[id] = 'placed';
            return memo;
          }, {})
        }));
        break;

      default:
        break;
    }
  }, [lastMessage])

  React.useEffect(() => {
    const [partial, total] = processSummaryContent(summary);

    if (total > 0 && partial === total) {
      clearInterval(ticker.current);
    }
  }, [summary])

  const starter = useMutation(content => startCheck(content).then(id => setJobID(id)))
  const { data } = useQuery(['content', jobID], async () => {
    const result = await checkResults(jobID)
    return result
  }, {
    enabled: contentEnabled,
    onSuccess: (data) => {
      if (data && data.status && data.status === 'done') {
        setIsLoading(false)
      }
    },
    select: selectProperData
  })

  const isDisabled = isLoading

  function clearCurrentCheck() {
    setContent('')
    setJobID('')
    setPercentage(0)
    setSummary({})
    setContentEnabled(false)
  }

  async function searchAndProcessContent(inputContent) {
    if (inputContent.length === 0) return;

    setContent(inputContent)
    setIsLoading(true)
    starter.mutate(inputContent)
  }

  return (
    <div className="pb-4">
      <div className="mb-4">
        <Input
          className="mb-4"
          bsSize="lg"
          type="textarea"
          value={content}
          disabled={isDisabled}
          rows={10}
          onChange={(e) => setContent(e.target.value)}
        />

        <StateButton
          color="primary"
          className="text-uppercase mb-4"
          onClick={() => searchAndProcessContent(content)}
          disabled={isDisabled}
        >
          Verificar
        </StateButton>

        {
          !isDisabled && (
            <StateButton
              color="link"
              onClick={() => clearCurrentCheck()}
            >
              Limpar texto
            </StateButton>
          )
        }

        {
          isDisabled && (
            <span className="mb-4 ml-4">
              { (percentage) ? `${percentage}% concluído. ${LONG_LABEL}` : 'Aguardando processos...' }
            </span>
          )
        }

        <BounceIndicador active={isDisabled} />
      </div>

      {
        !isDisabled && data && data.results && (
          <CheckerResults
            sentences={data.results.sentences}
            urls={data.results.urls}
            content={content}
          />
        )
      }
    </div>
  )
}

export default CopycheckerElement;
